/* eslint-disable default-case */
/* eslint-disable no-lone-blocks */
import { useRef, useEffect, useState } from "react";
import { VERTEX_SHADER, STARFIELD_FRAGMENT_SHADER, TUNNEL_FRAGMENT_SHADER, SPHERE_FRAGMENT_SHADER } from "./shaders.glsl";

const createShader = (gl, type, source) => {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error(gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
    }
    return shader;
};

const createWebGLProgram = (gl, vertShader, fragSource, width, height) => {
    const fragShader = createShader(gl, gl.FRAGMENT_SHADER, fragSource);
    const program = gl.createProgram();
    gl.attachShader(program, vertShader);
    gl.attachShader(program, fragShader);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        console.error(gl.getProgramInfoLog(program));
    }
    program.resolution = gl.getUniformLocation(program, "resolution");
    gl.useProgram(program);
    gl.uniform2f(program.resolution, width, height);
    program.uColors = gl.getUniformLocation(program, "uColors");
    return program;
};

const VISUALS = {
    "radial": { surface: "2d" },
    "bars": { surface: "2d" },
    "arcs": { surface: "2d" },
    "phoria3d": { surface: "2d" },
    //"curves": { surface: "2d" },
    "starfield": { surface: "webgl" },
    "tunnel": { surface: "webgl" },
    "sphere": { surface: "webgl" },
};

const Visualizer = ({audioContext, isPlaying}) => {
    const visuals = Object.keys(VISUALS);
    const palettes = ["rgb", "grb", "brg", "rbg", "gbr", "bgr"];
    const shaderPalettes = [
        [[2.85, 0.45, 0.20], [0.35, 1.15, 0.35], [0.45, 0.55, 1.95]],  // red, green, blue
        [[1.85, 1.55, 0.20], [1.65, 0.25, 1.55], [0.20, 1.55, 1.65]],  // yellow, magenta, cyan
        [[1.85, 1.55, 0.20], [1.85, 0.75, 0.20], [0.75, 0.20, 1.85]],  // yellow, orange, violet
        [[1.85, 0.45, 1.25], [0.20, 1.55, 1.65], [0.75, 1.85, 0.25]],  // pink, cyan, lime
        [[0.20, 1.55, 1.45], [1.85, 0.55, 0.45], [1.85, 1.35, 0.20]],  // teal, coral, gold
        [[0.85, 0.20, 1.85], [0.20, 1.65, 1.35], [1.85, 0.65, 0.95]],  // purple, aqua, rose
    ];
    const canvasRef = useRef(null);
    const canvas3dRef = useRef(null);
    const [renderer, setRenderer] = useState(null);
    const [starfieldProgram, setStarfieldProgram] = useState(null);
    const [tunnelProgram, setTunnelProgram] = useState(null);
    const [sphereProgram, setSphereProgram] = useState(null);
    const [audioTex, setAudioTex] = useState(null);
    const [canvas2dToggled, setCanvas2dToggled] = useState(false);

    // static variables for phoria 3d visualizer - don"t want to regenerate these on every frame
    const Phoria = window.Phoria;
    const vec4 = window.vec4;
    const lights = [
        Phoria.DistantLight.create({ direction: { x: 0, y: -20, z: -10 }, intensity: 0.8 }),
    ];
    const plane3d = Phoria.Util.generateTesselatedPlane(16,16,0,15);

    // reorder the values from an array of colours based on a palette string of "r", "g", "b" letters in any order
    const rgb = (colours, palette) => {
        return [
            colours[palette.indexOf("r")],
            colours[palette.indexOf("g")],
            colours[palette.indexOf("b")]
        ];
    };

    useEffect(() => {
        // Setup one time Phoria renderer linked to our canvas ref
        console.log("Initializing one-time renderer data for WebGL visuals and Phoria 3D visualizer");
        setRenderer(new Phoria.CanvasRenderer(canvasRef.current));

        // Setup one time WebGL context and shaders for webgl visuals
        const canvas = canvas3dRef.current;
        const gl = canvas.getContext("webgl");
        gl.getExtension("OES_standard_derivatives");

        // Shared vertex shader for all webgl visuals
        const vertShader = createShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER);

        // Build starfield WebGL program
        const sProgram = createWebGLProgram(gl, vertShader, STARFIELD_FRAGMENT_SHADER, canvas.width, canvas.height);
        setStarfieldProgram(sProgram);

        // Build tunnel WebGL program
        const tProgram = createWebGLProgram(gl, vertShader, TUNNEL_FRAGMENT_SHADER, canvas.width, canvas.height);
        setTunnelProgram(tProgram);

        // Build sphere WebGL program
        const spProgram = createWebGLProgram(gl, vertShader, SPHERE_FRAGMENT_SHADER, canvas.width, canvas.height);
        setSphereProgram(spProgram);

        // shared setup for WebGL audio texture - we update the texture data every frame with audio frequency data
        const audioTex = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, audioTex);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        setAudioTex(audioTex);
    }, []);
    
    const firstTime = Date.now();
    const draw = () => {
        audioContext.analyser.getByteFrequencyData(audioContext.dataArray);

        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        const canvas3d = canvas3dRef.current;
        const gl = canvas3d.getContext("webgl");

        // Helper to render WebGL shaders with common setup
        const renderWebGLShader = (shaderProgram) => {
            setCanvas2dToggled(false);
            const vertices = new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]);
            gl.useProgram(shaderProgram);

            // uniforms
            const time = (Date.now() - firstTime) / 1000.0;
            shaderProgram.time = gl.getUniformLocation(shaderProgram, "time");
            gl.uniform1f(shaderProgram.time, time);

            // pass colour palette as array of 3 vec3 values
            const sfPal = shaderPalettes[audioContext.palette ?? 0];
            const colorsFlat = new Float32Array([...sfPal[0], ...sfPal[1], ...sfPal[2]]);
            gl.uniform3fv(shaderProgram.uColors, colorsFlat);

            // Update audio data - apply to texture (1D texture simulated as 2D with height=1)
            const uAudioTex = gl.getUniformLocation(shaderProgram, "uAudioTex");
            gl.activeTexture(gl.TEXTURE0);
            gl.bindTexture(gl.TEXTURE_2D, audioTex);
            gl.uniform1i(uAudioTex, 0);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.LUMINANCE, audioContext.dataArray.length, 1, 0, gl.LUMINANCE, gl.UNSIGNED_BYTE, audioContext.dataArray);

            // prep full screen quad
            const buffer = gl.createBuffer();
            gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
            gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);
            const aPositionLoc = gl.getAttribLocation(shaderProgram, "aPosition");
            gl.enableVertexAttribArray(aPositionLoc);
            gl.vertexAttribPointer(aPositionLoc, 2, gl.FLOAT, false, 0, 0);

            // clear and draw
            gl.viewport(0, 0, canvas3d.width, canvas3d.height);
            gl.clearColor(0.0, 0.0, 0.0, 0.0);
            gl.clear(gl.COLOR_BUFFER_BIT);
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, vertices.length * 0.5);
        };

        // eslint-disable-next-line default-case
        switch (audioContext.visual) {
            case "starfield": {
                renderWebGLShader(starfieldProgram);
            } break;

            case "tunnel": {
                renderWebGLShader(tunnelProgram);
            } break;

            case "sphere": {
                renderWebGLShader(sphereProgram);
            } break;

            case "bars": {
                setCanvas2dToggled(true);
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                const barWidth = canvas.width / audioContext.bufferLength;
                let x = 0;
                for (let i = 0; i < audioContext.bufferLength; i++) {
                    const barHeight = (canvas.height / 256) * audioContext.dataArray[i];
                    const colours = [barHeight + 5, i * 4, barHeight / 4 - 12];
                    const palette = rgb(colours, palettes[audioContext.palette]);
                    ctx.fillStyle = `rgb(${palette[0]},${palette[1]},${palette[2]})`;
                    ctx.fillRect(x, canvas.height*0.65 - barHeight / 2, barWidth, barHeight / 2);
                    x += barWidth + 1;
                }
            } break;

            case "arcs": {
                setCanvas2dToggled(true);
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                const THIRDRAD = Math.PI * 0.666;
                const cx = canvas.width / 2;
                const cy = canvas.height / 2;
                const maxRadius = Math.min(canvas.width, canvas.height) / 2 * 0.7;
                const arcWidth = maxRadius / audioContext.bufferLength;
                ctx.save();
                ctx.globalCompositeOperation = "lighter";
                ctx.transform(1, 0, 0, 1, cx, cy);                  // Move origin to center
                ctx.rotate(Math.sin(Date.now()/3000)*2*Math.PI);    // Rotate canvas for extra chaos
                ctx.transform(1, 0, 0, 1, -cx, -cy);                // Move origin back
                for (let i = 0; i < audioContext.bufferLength; i++) {
                    const radius = arcWidth * i;
                    const arcDistance = (maxRadius / 256) * audioContext.dataArray[i];
                    const colours = [audioContext.dataArray[i], i * 2, (arcDistance / 8) + 128 + (Math.sin(Date.now()/100)*100)%255];
                    const palette = rgb(colours, palettes[audioContext.palette]);
                    ctx.strokeStyle = `rgb(${palette[0]},${palette[1]},${palette[2]})`;
                    ctx.lineWidth = arcWidth * 1.5;
                    const barLength = THIRDRAD * (audioContext.dataArray[i] / 256);  // length of arc based on frequency
                    // 3 arcs per frequency, spaced evenly around the circle
                    ctx.beginPath();
                    const arcOffset = (THIRDRAD - barLength) * 0.5; // center the arc within its third
                    ctx.arc(cx, cy, radius + arcDistance * 0.5, arcOffset, arcOffset + barLength);
                    ctx.stroke();
                    ctx.beginPath();
                    ctx.arc(cx, cy, radius + arcDistance * 0.5, THIRDRAD + arcOffset, THIRDRAD + arcOffset + barLength);
                    ctx.stroke();
                    ctx.beginPath();
                    ctx.arc(cx, cy, radius + arcDistance * 0.5, THIRDRAD + THIRDRAD + arcOffset, THIRDRAD + THIRDRAD + arcOffset + barLength);
                    ctx.stroke();
                }
                ctx.restore();
            } break;

            case "radial": {
                setCanvas2dToggled(true);
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                const cx = canvas.width / 2;
                const cy = canvas.height / 2;
                const radius = 1; // Base radius before frequency is added
                const barWidth = (2.25 * Math.PI) / audioContext.bufferLength * 2; // Angle step per bar
                ctx.save();
                ctx.globalCompositeOperation = "lighter";           // Blend mode for chaos
                ctx.transform(1, 0, 0, 1, cx, cy);                  // Move origin to center
                ctx.rotate(Math.sin(Date.now()/5000)*2*Math.PI);    // Rotate canvas for extra chaos
                for (let i = 0; i < audioContext.bufferLength; i++) {
                    // Interleave freq indices: even positions → low freqs, odd → high freqs
                    // distributes bass bars evenly around the circle instead of clustering them
                    //const fi = i % 2 === 0 ? (i >> 1) : (audioContext.bufferLength - 1 - (i >> 1));
                    //let barHeight = audioContext.dataArray[fi] / 128 * cx * 0.5;    // Scale frequency to length
                    let barHeight = audioContext.dataArray[i] / 128 * cx * 0.5;
                    let angle = i * barWidth + (Math.PI);
    
                    let x1 = Math.cos(angle) * radius;
                    let y1 = Math.sin(angle) * radius;
                    let x2 = Math.cos(angle) * (radius + barHeight);
                    let y2 = Math.sin(angle) * (radius + barHeight);
    
                    const colours = [barHeight + 25, i * 4, (barHeight / 8) + 128 + (Math.sin(Date.now()/100)*100)%255];
                    const palette = rgb(colours, palettes[audioContext.palette]);
                    ctx.strokeStyle = `rgb(${palette[0]},${palette[1]},${palette[2]})`;
                    ctx.lineWidth = 8 + (Math.random()*1.0);
                    ctx.beginPath();
                    ctx.moveTo(x1, y1);
                    ctx.lineTo(x2, y2);
                    ctx.stroke();
                }
                ctx.restore();
            } break;

            case "phoria3d": {
                setCanvas2dToggled(true);
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                const bars = [];
                const scene = new Phoria.Scene();
                scene.camera.position = { x: 0, y: 25, z: 20 };
                scene.camera.lookat = { x: 0, y: 0, z: 0 };
                scene.viewport = { x: 0, y: 0, width: canvas.width, height: canvas.height };
                scene.perspective.aspect = canvas.width / canvas.height;
                scene.graph = [...lights];
                const plane = Phoria.Entity.create({
                    points: [...plane3d.points],
                    edges: plane3d.edges,
                    style: {
                        drawmode: "wireframe",
                        shademode: "plain",
                        linewidth: 0.5,
                        objectsortmode: "back"
                    }
                });
                scene.graph.push(plane);

                const radius = 8;
                for (let i = 0; i < audioContext.bufferLength; i++) {
                    const angle = (i / audioContext.bufferLength) * 2 * Math.PI + (Math.sin(Date.now()/1000)*2);
                    const x = Math.cos(angle) * radius;
                    const z = Math.sin(angle) * radius;
                    
                    let barHeight = audioContext.dataArray[i] / 128 * canvas.width * 0.5;
                    const colours = [barHeight + 25, i * 2, (barHeight / 8) + 128];
                    const palette = rgb(colours, palettes[audioContext.palette]);
                    const unitCube = Phoria.Util.generateUnitCube(0.2);
                    const cube = Phoria.Entity.create({
                        points: unitCube.points,
                        edges: unitCube.edges,
                        polygons: unitCube.polygons,
                        style: {
                            color: palette,
                            drawmode: "solid",
                            shademode: "lightsource"
                        }
                    });
                    cube.translateX(x).translateZ(z);
                    bars.push(cube);

                    scene.graph.push(cube);
                }
                plane.rotateY(Math.sin(Date.now()/1000)*Math.PI);

                bars.forEach((bar, i) => {
                    const scale = audioContext.dataArray[i] * 0.1;
                    bar.scale(vec4.fromValues(0.5, Math.max(0.1, scale), 0.5, 1));
                });

                scene.modelView();
                renderer.render(scene);
            } break;

            case "curves": {
                setCanvas2dToggled(true);
                ctx.clearRect(0, 0, canvas.width, canvas.height);

                const f1 = (t) => {
                    return {
                        x: 12*Math.cos(t) - 6*Math.cos(11/6*t),
                        y: 12*Math.sin(t) - 6*Math.sin(11/6*t)
                    };
                }

                const time = Date.now() / 1600;
                const mix = Math.sin(time);
                const cx = canvas.width / 2;
                const cy = canvas.height / 2;
                ctx.save();
                ctx.lineWidth = 3;
                ctx.globalCompositeOperation = "lighter";
                ctx.translate(cx, cy);
                ctx.rotate(time%(Math.PI*2)*(1-mix));
                let pxy = {x:0, y:0};    // need to keep track of previous point for path closure
                for (let i=0,xy,t=0; t<Math.PI*24; t+=Math.PI/16,i++)
                {
                    if (i%2 === 1) {
                        // we flip each iteration to start or end a path
                        // counter intuitively, this means the first point is not closed - to avoid a line to the origin
                        ctx.beginPath();
                        ctx.lineTo(pxy.x * 10, -pxy.y * 10);
                    }
                    xy = f1(t);
                    const fi = i % 2 === 0 ? (i >> 1) : (audioContext.bufferLength - 1 - (i >> 1));
                    const freq = audioContext.dataArray[fi%audioContext.bufferLength];
                    const xn = Math.cos(t/12) * freq * 0.05;  // div by 12 to get a full rotation cycle of 2*PI radians
                    const yn = Math.sin(t/12) * (-freq) * 0.05;  // so relates to the Math.PI*24 loop limit!
                    xy.x += xn;
                    xy.y += yn;
                    ctx.lineTo(xy.x * 10, -xy.y * 10);
                    if (i%2 === 0) {
                        pxy = xy;
                        ctx.closePath();
                        const colours = [freq, i/2, (freq / 8) + 128];
                        const palette = rgb(colours, palettes[audioContext.palette]);
                        ctx.strokeStyle = `rgb(${palette[0]},${palette[1]},${palette[2]})`;
                        ctx.shadowColor = ctx.strokeStyle;
                        ctx.shadowBlur = freq / 16;
                        ctx.stroke();
                    }
                }
                ctx.restore();
            } break;
        }

        requestAnimationFrame(draw);
    };

    useEffect(() => {
        if (audioContext && !audioContext.visual) {
            // Initialize the visualizer with a random visual and palette
            //audioContext.visual = visuals[visuals.length-1];   // TEMP: default while testing
            audioContext.visual = visuals[Math.floor(Math.random() * (visuals.length-1))];
            audioContext.palette = Math.floor(Math.random() * palettes.length);
            console.log("Visualizer: init visual", audioContext.visual);
            draw();
        }
    }, [audioContext]);

    const canvasForVisual = (visual) => VISUALS[visual].surface === "webgl" ? canvas3dRef.current : canvasRef.current;

    const onCanvasClick = () => {
        if (audioContext && isPlaying) {
            const canvas = canvasForVisual(audioContext.visual);
            if (!document.fullscreenElement) {
                canvas.requestFullscreen();
            }
            else {
                document.exitFullscreen();
            }
        }
    };

    const cycleVisualizer = () => {
        // cycle through array of visual names
        if (audioContext) {
            audioContext.visual = visuals[(visuals.findIndex(v => v === audioContext.visual) + 1) % visuals.length];
            // when switching visual, ensure we"re using the correct canvas and fullscreen
            if (document.fullscreenElement) {
                document.exitFullscreen().then(() => {
                    const canvas = canvasForVisual(audioContext.visual);
                    canvas.requestFullscreen();
                });
            }
        }
    };

    const cyclePalette = () => {
        // cycle through array of pallete modes
        if (audioContext) {
            audioContext.palette = (audioContext.palette + 1) % palettes.length;
        }
    };

    window.onkeydown = (e) => {
        switch (e.key) {
            case 27:
                if (document.fullscreenElement) {
                    document.exitFullscreen();
                }
                break;
            case "v":
                cycleVisualizer();
                break;
            case "c":
                cyclePalette();
                break;
        }
    };

    return (
        <div className="visualizer-container">
            {audioContext ?
            <span>
                <button className="visualizer-button" onClick={()=> cycleVisualizer()} title="visual">⭗</button>
                <button className="visualizer-button" onClick={()=> cyclePalette()} title="palette">🎨</button>
            </span>
            : ""}
            <div className="canvas-wrapper">
                <canvas className={canvas2dToggled ? "hidden" : "visible"} ref={canvas3dRef} width="1000" height="1000" onClick={()=> onCanvasClick()} />
                <canvas className={canvas2dToggled ? "visible" : "hidden"} ref={canvasRef} width="600" height="600" onClick={()=> onCanvasClick()} />
            </div>
        </div>
    );
};

export default Visualizer;